import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import { useRouter } from "vue-router";

const EDGE_GAP = 24;
const MOBILE_GAP = 16;
const CLEARANCE = 16;
const MOBILE_BREAKPOINT = 640;

interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
  fullWidth: boolean;
}

const measureRect = (selector: string): DOMRect | null => {
  const element = document.querySelector(selector);

  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0 ? rect : null;
};

export const computeInsets = (
  vertical: "top" | "bottom",
  horizontal: "left" | "right",
  viewport: { width: number; height: number },
  rects: { topbar: DOMRect | null; actionBar: DOMRect | null; sidebar: DOMRect | null },
): Insets => {
  const isMobile = viewport.width < MOBILE_BREAKPOINT;
  const gap = isMobile ? MOBILE_GAP : EDGE_GAP;
  const insets: Insets = { top: gap, bottom: gap, left: gap, right: gap, fullWidth: isMobile };

  if (vertical === "top" && rects.topbar) {
    insets.top = Math.max(rects.topbar.bottom, 0) + CLEARANCE;
  }

  if (vertical === "bottom" && rects.actionBar && rects.actionBar.top < viewport.height) {
    insets.bottom = Math.max(viewport.height - rects.actionBar.top, 0) + CLEARANCE;
  }

  if (!isMobile && horizontal === "left" && rects.sidebar && rects.sidebar.right > 0) {
    insets.left = rects.sidebar.right + EDGE_GAP;
  }

  return insets;
};

export const useToastInsets = (
  vertical: Ref<"top" | "bottom">,
  horizontal: Ref<"left" | "right">,
  toastsCount: () => number,
) => {
  const router = useRouter();

  const insets = ref<Insets>({
    top: EDGE_GAP,
    bottom: EDGE_GAP,
    left: EDGE_GAP,
    right: EDGE_GAP,
    fullWidth: false,
  });

  let frameId: number | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const update = (): void => {
    insets.value = computeInsets(
      vertical.value,
      horizontal.value,
      { width: window.innerWidth, height: window.innerHeight },
      {
        topbar: measureRect(".sv-topbar"),
        actionBar: measureRect(".cal2-footer"),
        sidebar: measureRect("#sv-sidebar"),
      },
    );
  };

  const scheduleUpdate = (): void => {
    if (frameId !== null) {
      return;
    }

    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      update();
    });
  };

  const removeAfterEach = router.afterEach(() => {
    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 400);
  });

  onMounted(() => {
    update();

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, { capture: true, passive: true });

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(document.body);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", scheduleUpdate);
    window.removeEventListener("scroll", scheduleUpdate, { capture: true });
    resizeObserver?.disconnect();
    removeAfterEach();

    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
  });

  watch([vertical, horizontal, toastsCount], scheduleUpdate);

  const insetStyle = computed(() => {
    const value = insets.value;
    const horizontalStyle = value.fullWidth
      ? { left: `${value.left}px`, right: `${value.right}px` }
      : { [horizontal.value]: `${value[horizontal.value]}px` };

    return {
      [vertical.value]: `${value[vertical.value]}px`,
      ...horizontalStyle,
    };
  });

  return { insets, insetStyle, update };
};
